import React, { useState, useEffect } from 'react';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTheme } from '../contexts/ThemeContext';
import { BarChart, LineChart, PieChart, AreaChart } from './charts';
import ModuleContainer from './ModuleContainer';
import ThemeSelector from './ThemeSelector';

const DatabaseDashboard = () => {
  const { fetchData, refreshData, exportData, isLoading, getError } = useDatabase();
  const { theme } = useTheme();

  useEffect(() => {
    document.title = 'Sydney Ferris - Database Dashboard';
  }, []);
  const [modules, setModules] = useState([
    {
      id: 'sales-chart',
      type: 'Chart',
      chartType: 'bar',
      title: 'Sales Performance',
      moduleId: 'charts',
      dataIndex: 0,
      showLabels: true,
      animate: true
    },
    {
      id: 'user-metrics',
      type: 'Chart',
      chartType: 'line',
      title: 'User Growth',
      moduleId: 'charts',
      dataIndex: 1,
      showLabels: true,
      animate: true
    },
    {
      id: 'revenue-chart',
      type: 'Chart',
      chartType: 'area',
      title: 'Revenue Trends',
      moduleId: 'charts',
      dataIndex: 2,
      showLabels: true,
      opacity: 0.4
    },
    {
      id: 'distribution-chart',
      type: 'Chart',
      chartType: 'pie',
      title: 'Revenue Distribution',
      moduleId: 'charts',
      dataIndex: 0,
      showLabels: true,
      showLegend: true
    },
    {
      id: 'performance-chart',
      type: 'Chart',
      chartType: 'line',
      title: 'Performance Metrics',
      moduleId: 'analytics',
      dataIndex: 0,
      showLabels: true,
      animate: true
    },
    {
      id: 'analytics-metrics',
      type: 'Metrics',
      title: 'Key Metrics',
      endpoint: '/api/analytics',
      params: { type: 'userMetrics' },
      config: {
        layout: 'horizontal',
        showFooter: true
      }
    },
    {
      id: 'user-table',
      type: 'Table',
      title: 'User Management',
      endpoint: '/api/users',
      params: {},
      config: {
        columns: [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role' },
          { key: 'status', label: 'Status' },
          { key: 'lastSeen', label: 'Last Seen' }
        ],
        sortable: true,
        filterable: true,
        showFooter: true
      }
    },
    {
      id: 'activity-feed',
      type: 'Feed',
      title: 'Activity Feed',
      endpoint: '/api/feed',
      params: {},
      config: {
        maxItems: 5,
        showFooter: true
      }
    },
    {
      id: 'product-cards',
      type: 'Card',
      title: 'Product Overview',
      endpoint: '/api/tables',
      params: { table: 'products' },
      config: {
        layout: 'grid',
        size: 'medium',
        showFooter: true
      }
    },
    {
      id: 'performance-list',
      type: 'List',
      title: 'Performance Indicators',
      endpoint: '/api/analytics',
      params: { type: 'performance' },
      config: {
        type: 'default',
        selectable: true,
        showFooter: true
      }
    }
  ]);

  const [moduleData, setModuleData] = useState({});

  // Load data for all modules on mount
  useEffect(() => {
    loadAllModuleData();
  }, []);

  const loadAllModuleData = async () => {
    const dataPromises = modules.map(async (module) => {
      try {
        const data = await fetchData(module.endpoint, module.params);
        return { [module.id]: data };
      } catch (error) {
        console.error(`Failed to load data for ${module.id}:`, error);
        return { [module.id]: null };
      }
    });

    const results = await Promise.all(dataPromises);
    const combinedData = results.reduce((acc, result) => ({ ...acc, ...result }), {});
    setModuleData(combinedData);
  };

  const handleRefresh = async (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      try {
        const data = await refreshData(module.endpoint, module.params);
        setModuleData(prev => ({ ...prev, [moduleId]: data }));
      } catch (error) {
        console.error(`Failed to refresh ${moduleId}:`, error);
      }
    }
  };

  const handleExport = async (moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      try {
        await exportData(module.endpoint, module.params, 'json');
      } catch (error) {
        console.error(`Failed to export ${moduleId}:`, error);
      }
    }
  };

  const handleConfig = (moduleId) => {
    console.log(`Configure module: ${moduleId}`);
    // Future: Open configuration modal
  };

  const addModule = () => {
    const chartTypes = ['bar', 'line', 'pie', 'area'];
    const randomChartType = chartTypes[Math.floor(Math.random() * chartTypes.length)];
    
    const newModule = {
      id: `custom-${Date.now()}`,
      type: 'Chart',
      chartType: randomChartType,
      title: `New ${randomChartType.charAt(0).toUpperCase() + randomChartType.slice(1)} Chart`,
      moduleId: 'charts',
      dataIndex: 0,
      showLabels: true,
      animate: true
    };
    
    setModules(prev => [...prev, newModule]);
    
    // Load data for new module
    fetchData('/api/charts', { type: 'sales' })
      .then(data => setModuleData(prev => ({ ...prev, [newModule.id]: data })))
      .catch(error => console.error('Failed to load new module data:', error));
  };

  const removeModule = (moduleId) => {
    setModules(prev => prev.filter(m => m.id !== moduleId));
    setModuleData(prev => {
      const newData = { ...prev };
      delete newData[moduleId];
      return newData;
    });
  };

  const getModuleLayout = () => {
    const layout = {
      charts: modules.filter(m => m.type === 'Chart'),
      tables: modules.filter(m => m.type === 'Table'),
      metrics: modules.filter(m => m.type === 'Metrics'),
      feeds: modules.filter(m => m.type === 'Feed'),
      cards: modules.filter(m => m.type === 'Card'),
      lists: modules.filter(m => m.type === 'List')
    };
    
    return layout;
  };

  // Render chart component based on type
  const renderChart = (module) => {
    const { chartType, title, moduleId, dataIndex, showLabels, animate, opacity, showLegend } = module;
    
    switch (chartType) {
      case 'bar':
        return (
          <BarChart 
            title={title}
            moduleId={moduleId}
            dataIndex={dataIndex}
            showLabels={showLabels}
            animate={animate}
          />
        );
      case 'line':
        return (
          <LineChart 
            title={title}
            moduleId={moduleId}
            dataIndex={dataIndex}
            showLabels={showLabels}
            animate={animate}
          />
        );
      case 'pie':
        return (
          <PieChart 
            title={title}
            moduleId={moduleId}
            dataIndex={dataIndex}
            showLabels={showLabels}
            showLegend={showLegend}
          />
        );
      case 'area':
        return (
          <AreaChart 
            title={title}
            moduleId={moduleId}
            dataIndex={dataIndex}
            showLabels={showLabels}
            opacity={opacity}
          />
        );
      default:
        return (
          <BarChart 
            title={title}
            moduleId={moduleId}
            dataIndex={dataIndex}
            showLabels={showLabels}
            animate={animate}
          />
        );
    }
  };

  const layout = getModuleLayout();

  return (
    <div className="min-h-screen theme-bg-secondary">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold theme-primary">Database Dashboard</h1>
              <p className="theme-secondary mt-1 text-sm sm:text-base">
                Real-time data visualization with configurable modules
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <ThemeSelector />
              <button
                onClick={loadAllModuleData}
                className="px-3 py-2 sm:px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-xs sm:text-sm"
              >
                Refresh All
              </button>
              <button
                onClick={addModule}
                className="px-3 py-2 sm:px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm"
              >
                Add Module
              </button>
            </div>
          </div>
        </div>

        {/* Module Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {modules.map((module) => (
            <div key={module.id} className="relative">
              <ModuleContainer
                moduleType={module.type}
                moduleId={module.id}
                title={module.title}
                data={module.type === 'Chart' ? renderChart(module) : moduleData[module.id]}
                config={module.config}
                isLoading={module.type === 'Chart' ? false : isLoading(module.endpoint, module.params)}
                error={module.type === 'Chart' ? null : getError(module.endpoint, module.params)}
                onRefresh={handleRefresh}
                onExport={handleExport}
                onConfig={handleConfig}
              />

              {/* Remove Module Button */}
              <button
                onClick={() => removeModule(module.id)}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 hover:opacity-100 transition-opacity"
                title="Remove Module"
              >
                <span className="text-xs">×</span>
              </button>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {modules.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-gray-400 text-4xl sm:text-6xl mb-4">📊</div>
            <h3 className="text-base sm:text-lg font-medium theme-primary mb-2">No modules configured</h3>
            <p className="theme-secondary mb-4 text-sm sm:text-base">Add your first module to get started</p>
            <button
              onClick={addModule}
              className="px-4 py-2 sm:px-6 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm sm:text-base"
            >
              Add Module
            </button>
          </div>
        )}

        {/* Module Configuration Panel */}
        <div className="mt-6 sm:mt-8 theme-bg rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold theme-primary mb-4">Module Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {modules.map((module) => (
              <div key={module.id} className="p-3 sm:p-4 border theme-border-secondary rounded-lg">
                <h3 className="font-medium theme-primary text-sm sm:text-base">{module.title}</h3>
                <p className="text-xs sm:text-sm theme-secondary mt-1">Type: {module.type}</p>
                <p className="text-xs sm:text-sm theme-secondary">Endpoint: {module.endpoint}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => handleRefresh(module.id)}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={() => handleExport(module.id)}
                    className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                  >
                    Export
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseDashboard;
